import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import GuestOverlay from "@/components/auth/GuestOverlay";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { toast as sonnerToast } from "sonner";
import {
    Layers,
    Loader2,
    Video,
    Play,
    CheckCircle2,
    AlertTriangle,
    Download,
    Copy,
    Trash2,
    ChevronUp,
    ChevronDown,
    Clock,
    ExternalLink,
    History,
    RefreshCw,
    Plus,
    ArrowRight,
    Stamp,
    Type,
    Move,
    Eye,
    Upload,
    ImageIcon as ImageIconLucide,
    X,
    Info
} from "lucide-react";


const WATERMARK_API_URL = "https://wm.gencnx.com";
const MERGE_CREDITS = 10;
const EXPIRY_HOURS = 72;
const CONFIG_KEY = "watermark_config_v1";

interface GenerationLog {
    id: string;
    service_type: string;
    model: string;
    result_url: string | null;
    status: string;
    created_at: string;
}

interface MergeJobResult {
    status: "idle" | "queued" | "processing" | "done" | "failed" | "error";
    jobId?: string;
    resultUrl?: string;
    message?: string;
}

interface WatermarkConfig {
    enableText: boolean;
    text: string;
    textSize: number;
    textLocation: string;
    textColor: string;
    textOpacity: number;
    textMargin: number;
    enableImage: boolean;
    imageUrl: string;
    imageSize: number;
    imageLocation: string;
    imageOpacity: number;
    imageMargin: number;
}

interface SavedWatermarkConfig {
    enableText: boolean;
    text: string;
    textSize: number;
    textLocation: string;
    textColor: string;
    textOpacity: number;
    textMargin: number;
    enableImage: boolean;
    imageUrl: string;
    imageSize: number;
    imageLocation: string;
    imageOpacity: number;
    imageMargin: number;
}

const locationGrid = [
    [
        { id: "top-left", labelTh: "↖", labelEn: "↖" },
        { id: "top-center", labelTh: "↑", labelEn: "↑" },
        { id: "top-right", labelTh: "↗", labelEn: "↗" }
    ],
    [
        { id: "center-left", labelTh: "←", labelEn: "←" },
        { id: "center", labelTh: "●", labelEn: "●" },
        { id: "center-right", labelTh: "→", labelEn: "→" }
    ],
    [
        { id: "bottom-left", labelTh: "↙", labelEn: "↙" },
        { id: "bottom-center", labelTh: "↓", labelEn: "↓" },
        { id: "bottom-right", labelTh: "↘", labelEn: "↘" }
    ]
];

const colorOptions = [
    { id: "white", labelTh: "ขาว", labelEn: "White", value: "#FFFFFF" },
    { id: "black", labelTh: "ดำ", labelEn: "Black", value: "#000000" },
    { id: "red", labelTh: "แดง", labelEn: "Red", value: "#FF0000" },
    { id: "yellow", labelTh: "เหลือง", labelEn: "Yellow", value: "#FFFF00" },
    { id: "blue", labelTh: "น้ำเงิน", labelEn: "Blue", value: "#0066FF" },
    { id: "green", labelTh: "เขียว", labelEn: "Green", value: "#00FF00" }
];

const VideoMerge = () => {
    const { t } = useLanguage();
    const { user, profile, refreshProfile, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const { toast } = useToast();

    const [recentVideos, setRecentVideos] = useState<GenerationLog[]>([]);
    const [selectedVideos, setSelectedVideos] = useState<GenerationLog[]>([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [jobResult, setJobResult] = useState<MergeJobResult>({ status: "idle" });
    const [checkingStatus, setCheckingStatus] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const imageInputRef = useRef<HTMLInputElement>(null);

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !user) return;

        if (!file.type.startsWith('image/')) {
            toast({
                variant: "destructive",
                title: t("ไฟล์ไม่ถูกต้อง", "Invalid File"),
                description: t("กรุณาอัพโหลดไฟล์รูปภาพ", "Please upload an image file")
            });
            return;
        }

        setUploadingImage(true);
        try {
            const fileExt = file.name.split('.').pop();
            const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
            const filePath = `${user.id}/${fileName}`;

            const { error: uploadError } = await supabase.storage
                .from('generation-inputs')
                .upload(filePath, file);

            if (uploadError) throw uploadError;

            const { data: { publicUrl } } = supabase.storage
                .from('generation-inputs')
                .getPublicUrl(filePath);

            setWmConfig(prev => ({ ...prev, imageUrl: publicUrl }));
            toast({
                title: t("อัพโหลดสำเร็จ", "Upload Complete"),
                description: t("รูปภาพพร้อมใช้งานแล้ว", "Image is ready to use")
            });
        } catch (error: any) {
            console.error("Upload error:", error);
            toast({
                variant: "destructive",
                title: t("อัพโหลดล้มเหลว", "Upload Failed"),
                description: error.message
            });
        } finally {
            setUploadingImage(false);
        }
    };

    const [wmConfig, setWmConfig] = useState<WatermarkConfig>(() => {
        const defaultConfig: WatermarkConfig = {
            enableText: false,
            text: "",
            textSize: 32,
            textLocation: "bottom-center",
            textColor: "#FFFFFF",
            textOpacity: 70,
            textMargin: 8,
            enableImage: false,
            imageUrl: "",
            imageSize: 128,
            imageLocation: "top-left",
            imageOpacity: 80,
            imageMargin: 8
        };

        try {
            const saved = localStorage.getItem(CONFIG_KEY);
            if (saved) {
                const savedConfig: SavedWatermarkConfig = JSON.parse(saved);
                return { ...defaultConfig, ...savedConfig };
            }
        } catch (e) {
            console.error("Error loading saved config:", e);
        }
        return defaultConfig;
    });

    // Save config to localStorage when it changes
    useEffect(() => {
        const savedConfig: SavedWatermarkConfig = {
            enableText: wmConfig.enableText,
            text: wmConfig.text,
            textSize: wmConfig.textSize,
            textLocation: wmConfig.textLocation,
            textColor: wmConfig.textColor,
            textOpacity: wmConfig.textOpacity,
            textMargin: wmConfig.textMargin,
            enableImage: wmConfig.enableImage,
            imageUrl: wmConfig.imageUrl,
            imageSize: wmConfig.imageSize,
            imageLocation: wmConfig.imageLocation,
            imageOpacity: wmConfig.imageOpacity,
            imageMargin: wmConfig.imageMargin
        };
        localStorage.setItem(CONFIG_KEY, JSON.stringify(savedConfig));
    }, [wmConfig]);

    useEffect(() => {
        if (user) {
            fetchRecentVideos();
        }
    }, [user]);

    const fetchRecentVideos = async () => {
        if (!user) return;
        setIsLoadingHistory(true);
        try {
            const { data, error } = await supabase
                .from("generation_logs")
                .select("*")
                .eq("user_id", user.id)
                .in("service_type", ["video", "video_motion", "watermark"] as any[])
                .eq("status", "completed")
                .not("result_url", "is", null)
                .order("created_at", { ascending: false })
                .limit(30);

            if (error) throw error;

            // Filter out JSON results and duplicates
            const filtered = (data || []).filter(log => {
                const url = log.result_url;
                return url && !url.startsWith("{");
            });

            setRecentVideos(filtered);
        } catch (err) {
            console.error("Error fetching videos:", err);
        } finally {
            setIsLoadingHistory(false);
        }
    };

    const handleSelectVideo = (video: GenerationLog) => {
        if (selectedVideos.find(v => v.id === video.id)) {
            setSelectedVideos(prev => prev.filter(v => v.id !== video.id));
        } else if (selectedVideos.length < 5) {
            setSelectedVideos(prev => [...prev, video]);
        } else {
            sonnerToast.error(t("เลือกได้สูงสุด 5 วิดีโอ", "Select up to 5 videos"));
        }
    };

    const handleRemoveSelected = (id: string) => {
        setSelectedVideos(prev => prev.filter(v => v.id !== id));
    };

    const moveItem = (index: number, direction: 'up' | 'down') => {
        const newItems = [...selectedVideos];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex >= 0 && targetIndex < newItems.length) {
            [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
            setSelectedVideos(newItems);
        }
    };

    const handleSubmit = async () => {
        if (selectedVideos.length < 2) {
            sonnerToast.error(t("กรุณาเลือกวิดีโออย่างน้อย 2 ไฟล์", "Please select at least 2 videos"));
            return;
        }

        if (!profile || profile.credits < MERGE_CREDITS) {
            sonnerToast.error(t(`เครดิตไม่เพียงพอ ต้องการ ${MERGE_CREDITS} เครดิต`, `Insufficient credits. Need ${MERGE_CREDITS} credits`));
            return;
        }

        setIsProcessing(true);
        setJobResult({ status: "queued" });

        try {
            const videoUrls = selectedVideos.map(v => v.result_url).filter(Boolean) as string[];

            const requestBody: any = {
                video_urls: videoUrls,
                email: user?.email || "guest@gencnx.com"
            };

            // Add text watermark if enabled
            if (wmConfig.enableText && wmConfig.text.trim()) {
                requestBody.text = wmConfig.text;
                requestBody.text_size = wmConfig.textSize;
                requestBody.text_location = wmConfig.textLocation;
                requestBody.text_color = wmConfig.textColor;
                requestBody.text_opacity = wmConfig.textOpacity / 100;
                requestBody.text_margin = wmConfig.textMargin;
            }

            // Add image watermark if enabled
            if (wmConfig.enableImage && wmConfig.imageUrl) {
                requestBody.image_url = wmConfig.imageUrl;
                requestBody.image_size = wmConfig.imageSize;
                requestBody.image_location = wmConfig.imageLocation;
                requestBody.image_opacity = wmConfig.imageOpacity / 100;
                requestBody.image_margin = wmConfig.imageMargin;
            }

            // Use proxy to avoid CORS issues
            const { data: proxyResponse, error: proxyError } = await supabase.functions.invoke('watermark-api-proxy', {
                body: {
                    action: 'mergeVideos',
                    body: requestBody
                }
            });

            if (proxyError) {
                throw new Error(proxyError.message || 'Proxy request failed');
            }

            const data = proxyResponse;

            if (data.job_id) {
                setJobResult({
                    status: "queued",
                    jobId: data.job_id,
                    message: data.message || t("เริ่มรวมวิดีโอ...", "Starting merge...")
                });
                pollJobStatus(data.job_id);
            } else {
                throw new Error(data.error || data.message || "Merge failed");
            }
        } catch (err: any) {
            console.error("Merge API error:", err);
            setJobResult({ status: "error", message: err.message });
            setIsProcessing(false);
        }
    };

    const pollJobStatus = async (jobId: string) => {
        const maxAttempts = 120;
        let attempts = 0;

        const checkStatus = async () => {
            if (attempts >= maxAttempts) {
                setJobResult({ status: "error", message: t("หมดเวลาประมวลผล", "Timeout") });
                setIsProcessing(false);
                return;
            }

            try {
                // Use proxy to avoid CORS issues
                const { data: proxyResponse, error: proxyError } = await supabase.functions.invoke('watermark-api-proxy', {
                    body: {
                        action: 'getJobStatus',
                        jobId: jobId
                    }
                });

                if (proxyError) {
                    throw new Error(proxyError.message);
                }

                const data = proxyResponse;

                if (data.status === "done") {
                    setJobResult({
                        status: "done",
                        jobId: jobId,
                        resultUrl: data.output_url
                    });
                    setIsProcessing(false);

                    // Deduct credits and save to history
                    await saveToHistory(data.output_url);
                } else if (data.status === "failed") {
                    setJobResult({ status: "failed", message: data.error_message });
                    setIsProcessing(false);
                } else {
                    setJobResult(prev => ({ ...prev, status: data.status || "processing" }));
                    attempts++;
                    setTimeout(checkStatus, 5000);
                }
            } catch (err) {
                console.error("Status check error:", err);
                attempts++;
                setTimeout(checkStatus, 5000);
            }
        };
        checkStatus();
    };

    const saveToHistory = async (resultUrl: string) => {
        if (!user) return;
        try {
            const prompt = `Merge ${selectedVideos.length} videos: ${selectedVideos.map(v => v.id.substring(0, 4)).join(", ")}`;

            const { data, error } = await supabase.rpc('deduct_credits_for_generation' as any, {
                p_user_id: user.id,
                p_credits_required: MERGE_CREDITS,
                p_service_type: 'video',
                p_model: 'Video Merge',
                p_prompt: prompt
            });

            if (error) throw error;
            const result = data as { success: boolean; log_id?: string; error?: string };

            if (result.success && result.log_id) {
                await supabase
                    .from("generation_logs")
                    .update({
                        result_url: resultUrl,
                        status: "completed",
                        completed_at: new Date().toISOString()
                    })
                    .eq("id", result.log_id);

                await refreshProfile();
                sonnerToast.success(t("รวมวิดีโอสำเร็จ!", "Videos merged successfully!"));
            }
        } catch (err) {
            console.error("Error saving history:", err);
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        toast({ title: t("คัดลอกแล้ว", "Copied") });
    };

    const handleDownload = async (url: string, filename: string) => {
        try {
            const response = await fetch(url, { mode: 'cors' });
            if (!response.ok) throw new Error('Fetch failed');
            const blob = await response.blob();
            if (blob.size === 0) throw new Error('Empty blob');
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error("Download failed, opening in new tab:", err);
            window.open(url, '_blank');
        }
    };

    if (authLoading) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

    return (
        <div className="min-h-screen bg-background flex flex-col">
            <Navbar />
            {!user && <GuestOverlay />}
            <main className="flex-1 pt-24 pb-16">
                <div className="container mx-auto px-4 max-w-6xl">
                    <div className="text-center mb-8">
                        <Badge variant="secondary" className="mb-4">
                            <Layers className="h-3 w-3 mr-1" />
                            {t("รวมวิดีโอ", "Video Merge")}
                        </Badge>
                        <h1 className="text-3xl md:text-4xl font-heading font-bold mb-2">
                            <span className="text-gradient">{t("รวมวิดีโอ", "Merge")}</span> {t("เข้าด้วยกัน", "Videos Together")}
                        </h1>
                        <p className="text-muted-foreground">{t("เลือกวิดีโอ 2-5 ไฟล์ เพื่อนำมารวมเป็นไฟล์เดียว", "Select 2-5 videos to merge into a single file.")}</p>
                    </div>

                    <div className="grid lg:grid-cols-3 gap-8">
                        {/* Selection Panel */}
                        <div className="lg:col-span-2 space-y-6">
                            <Card variant="glass">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-lg">
                                        <Video className="h-5 w-5 text-primary" />
                                        {t("วิดีโอของคุณ", "Your Videos")}
                                    </CardTitle>
                                    <CardDescription>{t("เลือกวิดีโอจากประวัติการสร้างล่าสุด (คลิกเพื่อเลือก/ยกเลิก)", "Select from your recent generations (Click to select/deselect)")}</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    {isLoadingHistory ? (
                                        <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
                                    ) : recentVideos.length === 0 ? (
                                        <div className="text-center py-12 text-muted-foreground">{t("ไม่พบวิดีโอในประวัติของคุณ", "No videos found in your history.")}</div>
                                    ) : (
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                            {recentVideos.map(video => {
                                                const isSelected = selectedVideos.find(v => v.id === video.id);
                                                return (
                                                    <div
                                                        key={video.id}
                                                        onClick={() => handleSelectVideo(video)}
                                                        className={`relative aspect-video rounded-xl overflow-hidden cursor-pointer border-2 transition-all ${isSelected ? 'border-primary ring-2 ring-primary/20' : 'border-transparent hover:border-primary/50'}`}
                                                    >
                                                        <video src={video.result_url || ""} className="w-full h-full object-cover" muted playsInline />
                                                        {isSelected && (
                                                            <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                                                                <Badge className="bg-primary text-white p-1 rounded-full"><CheckCircle2 className="h-5 w-5" /></Badge>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </div>

                        {/* Merge Control Panel */}
                        <div className="space-y-6">
                            <Card variant="glass">

                                <CardHeader>
                                    <CardTitle className="text-lg">{t("คิวการรวมวิดีโอ", "Merge Queue")}</CardTitle>
                                    <CardDescription>{t("จัดเรียงลำดับวิดีโอที่คุณเลือก", "Arrange the order of your selected videos")}</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    {selectedVideos.length === 0 ? (
                                        <div className="text-center py-8 border-2 border-dashed rounded-xl text-muted-foreground text-sm">
                                            <Plus className="h-8 w-8 mx-auto mb-2 opacity-30" />
                                            {t("ยังไม่ได้เลือกวิดีโอ", "No videos selected")}
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {selectedVideos.map((video, idx) => (
                                                <div key={video.id} className="flex items-center gap-3 p-2 bg-white/5 border border-white/10 rounded-lg group">
                                                    <div className="w-16 aspect-video rounded overflow-hidden flex-shrink-0">
                                                        <video src={video.result_url || ""} className="w-full h-full object-cover" muted />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-xs font-medium truncate">#{idx + 1} {video.model}</p>
                                                    </div>
                                                    <div className="flex items-center gap-1 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
                                                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => moveItem(idx, 'up')} disabled={idx === 0}><ChevronUp className="h-4 w-4" /></Button>
                                                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => moveItem(idx, 'down')} disabled={idx === selectedVideos.length - 1}><ChevronDown className="h-4 w-4" /></Button>
                                                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:bg-destructive/10" onClick={() => handleRemoveSelected(video.id)}><Trash2 className="h-4 w-4" /></Button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <div className="p-3 bg-primary/10 rounded-xl space-y-2">
                                        <div className="flex justify-between items-center text-sm font-medium">
                                            <span>{t("เครดิตที่ใช้", "Credits Required")}</span>
                                            <span>{MERGE_CREDITS} เครดิต</span>
                                        </div>
                                    </div>

                                    {/* Watermark Section */}
                                    <div className="pt-4 border-t border-white/10 space-y-6">
                                        <div className="flex items-center gap-2 text-sm font-semibold text-primary">
                                            <Stamp className="h-4 w-4" />
                                            {t("ตั้งค่าลายน้ำ (ระบุหรือไม่ก็ได้)", "Watermark Settings (Optional)")}
                                        </div>

                                        {/* Text Watermark */}
                                        <div className="space-y-4 p-4 bg-white/5 border border-white/10 rounded-xl">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <Type className="h-4 w-4 text-muted-foreground" />
                                                    <span className="text-sm font-medium">{t("ลายน้ำข้อความ", "Text Watermark")}</span>
                                                </div>
                                                <Switch
                                                    checked={wmConfig.enableText}
                                                    onCheckedChange={(val) => setWmConfig(prev => ({ ...prev, enableText: val }))}
                                                />
                                            </div>

                                            {wmConfig.enableText && (
                                                <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                                                    <div className="space-y-2">
                                                        <Label className="text-xs">{t("ข้อความ", "Text Content")}</Label>
                                                        <Input
                                                            placeholder={t("ระบุข้อความ...", "Enter text...")}
                                                            value={wmConfig.text}
                                                            onChange={(e) => setWmConfig(prev => ({ ...prev, text: e.target.value }))}
                                                            className="h-9"
                                                        />
                                                    </div>

                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div className="space-y-2">
                                                            <div className="flex justify-between">
                                                                <Label className="text-xs">{t("ขนาด", "Size")}</Label>
                                                                <span className="text-xs text-muted-foreground">{wmConfig.textSize}px</span>
                                                            </div>
                                                            <Slider
                                                                value={[wmConfig.textSize]}
                                                                min={12} max={120} step={1}
                                                                onValueChange={([val]) => setWmConfig(prev => ({ ...prev, textSize: val }))}
                                                            />
                                                        </div>
                                                        <div className="space-y-2">
                                                            <Label className="text-xs">{t("สี", "Color")}</Label>
                                                            <div className="flex flex-wrap gap-1.5">
                                                                {colorOptions.map(color => (
                                                                    <button
                                                                        key={color.id}
                                                                        className={`w-5 h-5 rounded-full border border-white/20 transition-transform ${wmConfig.textColor === color.value ? 'scale-125 ring-2 ring-primary ring-offset-1 ring-offset-background' : 'hover:scale-110'}`}
                                                                        style={{ backgroundColor: color.value }}
                                                                        onClick={() => setWmConfig(prev => ({ ...prev, textColor: color.value }))}
                                                                        title={t(color.labelTh, color.labelEn)}
                                                                    />
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="space-y-2">
                                                        <div className="flex justify-between">
                                                            <Label className="text-xs">{t("ตำแหน่ง", "Location")}</Label>
                                                        </div>
                                                        <div className="grid grid-cols-3 gap-1 w-24 mx-auto">
                                                            {locationGrid.flat().map(pos => (
                                                                <button
                                                                    key={pos.id}
                                                                    className={`w-7 h-7 flex items-center justify-center text-xs rounded border transition-colors ${wmConfig.textLocation === pos.id ? 'bg-primary text-white border-primary' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}
                                                                    onClick={() => setWmConfig(prev => ({ ...prev, textLocation: pos.id }))}
                                                                >
                                                                    {pos.labelTh}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Image Watermark */}
                                        <div className="space-y-4 p-4 bg-white/5 border border-white/10 rounded-xl">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <ImageIconLucide className="h-4 w-4 text-muted-foreground" />
                                                    <span className="text-sm font-medium">{t("ลายน้ำรูปภาพ/โลโก้", "Image/Logo Watermark")}</span>
                                                </div>
                                                <Switch
                                                    checked={wmConfig.enableImage}
                                                    onCheckedChange={(val) => setWmConfig(prev => ({ ...prev, enableImage: val }))}
                                                />
                                            </div>

                                            {wmConfig.enableImage && (
                                                <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                                                    <div className="space-y-2">
                                                        <input
                                                            type="file"
                                                            className="hidden"
                                                            ref={imageInputRef}
                                                            accept="image/*"
                                                            onChange={handleImageUpload}
                                                        />
                                                        {wmConfig.imageUrl ? (
                                                            <div className="relative aspect-video rounded-lg bg-black/40 border border-white/10 overflow-hidden group">
                                                                <img src={wmConfig.imageUrl} className="w-full h-full object-contain" alt="Logo" />
                                                                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center gap-2 transition-opacity">
                                                                    <Button size="icon" variant="secondary" className="h-8 w-8" onClick={() => imageInputRef.current?.click()}><Upload className="h-4 w-4" /></Button>
                                                                    <Button size="icon" variant="destructive" className="h-8 w-8" onClick={() => setWmConfig(prev => ({ ...prev, imageUrl: "" }))}><X className="h-4 w-4" /></Button>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <Button
                                                                variant="outline"
                                                                className="w-full h-20 border-dashed border-2 flex flex-col gap-1"
                                                                onClick={() => imageInputRef.current?.click()}
                                                                disabled={uploadingImage}
                                                            >
                                                                {uploadingImage ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
                                                                <span className="text-xs">{t("อัพโหลดโลโก้", "Upload Logo")}</span>
                                                            </Button>
                                                        )}
                                                    </div>

                                                    {wmConfig.imageUrl && (
                                                        <div className="space-y-4">
                                                            <div className="space-y-2">
                                                                <div className="flex justify-between">
                                                                    <Label className="text-xs">{t("ขนาด", "Size")}</Label>
                                                                    <span className="text-xs text-muted-foreground">{wmConfig.imageSize}px</span>
                                                                </div>
                                                                <Slider
                                                                    value={[wmConfig.imageSize]}
                                                                    min={32} max={400} step={1}
                                                                    onValueChange={([val]) => setWmConfig(prev => ({ ...prev, imageSize: val }))}
                                                                />
                                                            </div>
                                                            <div className="space-y-2">
                                                                <Label className="text-xs">{t("ตำแหน่ง", "Location")}</Label>
                                                                <div className="grid grid-cols-3 gap-1 w-24 mx-auto">
                                                                    {locationGrid.flat().map(pos => (
                                                                        <button
                                                                            key={pos.id}
                                                                            className={`w-7 h-7 flex items-center justify-center text-xs rounded border transition-colors ${wmConfig.imageLocation === pos.id ? 'bg-primary text-white border-primary' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}
                                                                            onClick={() => setWmConfig(prev => ({ ...prev, imageLocation: pos.id }))}
                                                                        >
                                                                            {pos.labelTh}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <Button
                                        variant="hero"
                                        className="w-full font-bold h-12"
                                        onClick={handleSubmit}
                                        disabled={isProcessing || selectedVideos.length < 2 || (profile?.credits || 0) < MERGE_CREDITS}
                                    >
                                        {isProcessing ? (
                                            <><Loader2 className="h-5 w-5 mr-2 animate-spin" />{t("กำลังรวมวิดีโอ...", "Merging...")}</>
                                        ) : (
                                            <><Layers className="h-5 w-5 mr-2" />{t("เริ่มรวมไฟล์", "Merge Now")}</>
                                        )}
                                    </Button>

                                    {/* Job Result */}
                                    {jobResult.status !== "idle" && (
                                        <div className="mt-6 pt-6 border-t border-white/10 space-y-4">
                                            {jobResult.status === "queued" || jobResult.status === "processing" ? (
                                                <div className="space-y-3">
                                                    <div className="flex items-center gap-2 text-primary font-medium">
                                                        <Loader2 className="h-4 w-4 animate-spin" />
                                                        {t("กำลังประมวลผล", "Processing")}
                                                    </div>
                                                    <p className="text-xs text-muted-foreground">{t("ระบบกำลังรวมวิดีโอ อาจใช้เวลา 1-3 นาที กรุณาอย่าปิดหน้านี้", "Merging videos. May take 1-3 minutes. Please stay on this page.")}</p>
                                                </div>
                                            ) : jobResult.status === "done" && jobResult.resultUrl ? (
                                                <div className="space-y-4">
                                                    <div className="flex items-center gap-2 text-green-500 font-bold">
                                                        <CheckCircle2 className="h-5 w-5" />
                                                        {t("เสร็จสิ้น!", "Success!")}
                                                    </div>
                                                    <video src={jobResult.resultUrl} controls className="w-full rounded-xl border border-white/20 shadow-lg" />
                                                    <div className="flex gap-2">
                                                        <Button
                                                            variant="glow"
                                                            className="flex-1"
                                                            onClick={() => handleDownload(jobResult.resultUrl!, "merged_video.mp4")}
                                                        >
                                                            <Download className="h-4 w-4 mr-2" />
                                                            {t("ดาวน์โหลด", "Download")}
                                                        </Button>
                                                        <Button variant="outline" size="icon" onClick={() => copyToClipboard(jobResult.resultUrl!)}><Copy className="h-4 w-4" /></Button>
                                                    </div>
                                                </div>
                                            ) : (jobResult.status === "failed" || jobResult.status === "error") && (
                                                <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-xl">
                                                    <div className="flex items-center gap-2 text-destructive font-medium mb-1">
                                                        <AlertTriangle className="h-4 w-4" />
                                                        {t("เกิดข้อผิดพลาด", "Error")}
                                                    </div>
                                                    <p className="text-xs text-destructive/80">{jobResult.message}</p>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div className="pt-6 border-t border-white/10">
                                        <div className="p-4 bg-primary/5 rounded-xl border border-primary/10">
                                            <h3 className="text-xs font-bold font-heading mb-2 flex items-center gap-2">
                                                <Info className="h-3 w-3 text-primary" />
                                                {t("คำแนะนำ", "Tips")}
                                            </h3>
                                            <div className="text-[10px] text-muted-foreground space-y-1.5">
                                                <p>• {t("เลือกวิดีโอ 2-5 ไฟล์", "Select 2-5 videos")}</p>
                                                <p>• {t("วิดีโอจะถูกจัดเรียงตามลำดับในคิว", "Videos will be merged in queue order")}</p>
                                                <p>• {t("เครดิตจะถูกหักเมื่อระบบเริ่มประมวลผล", "Credits are deducted when processing starts")}</p>
                                                <p>• {t("ไฟล์ที่รวมเสร็จแล้วจะถูกเก็บไว้ 3 วัน", "Merged files are kept for 3 days")}</p>
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            </main>
            <Footer />
        </div>

    );
};

export default VideoMerge;
